// ============================================================================
// CANCUN100 — PresentacionShow.js  (FASE PRESENTACIÓN 🎬)
// ----------------------------------------------------------------------------
// Overlay cinematográfico a pantalla completa para la junta de vendedores.
// SOLO LECTURA: recibe los datos ya agregados del periodo activo (ResumenTab).
// Autocontenido (colores/curvas/timings calcados del mockup aprobado por Marco).
// Cero dependencias nuevas, cero escrituras.
// ============================================================================
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { RADIO_AVATAR } from './tema';
import { Ic } from './iconos'; // APP 2/34: íconos Lucide en vez de emoji

const F0 = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 });
const money = (n) => '$' + F0.format(Number(n || 0));
const CONFETTI_COLORS = ['#f74780', '#ff8c42', '#ffd24a', '#22c55e', '#a855f7'];

const CSS = `
.pz-root{position:fixed;inset:0;z-index:2147483647;background:radial-gradient(120% 90% at 50% -12%,#1d2445 0%,#0b0e1a 62%);color:#fff;font-family:'DM Sans',system-ui,sans-serif;overflow:hidden;cursor:pointer;user-select:none}
.pz-center{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:24px}
.pz-eyebrow{font-size:clamp(13px,2.3vw,22px);letter-spacing:9px;text-transform:uppercase;color:#8ea0c8;margin-bottom:16px}
.pz-name{font-family:var(--au-font-text);font-weight:800;line-height:1;letter-spacing:2px}
.pz-amount{font-family:var(--au-font-text);font-weight:800;margin-top:16px}
.pz-desglose{margin-top:20px;font-size:clamp(13px,1.9vw,19px);color:#aab6d4;letter-spacing:1px}
.pz-grad{background:linear-gradient(135deg,#f74780,#ff8c42);-webkit-background-clip:text;background-clip:text;color:transparent}
.pz-gold{background:linear-gradient(135deg,#ffd24a,#fff6d8,#ffd24a);-webkit-background-clip:text;background-clip:text;color:transparent;text-shadow:0 0 40px rgba(255,210,74,.45)}
.pz-fade{animation:pzFade 1.1s ease both}
@keyframes pzFade{from{opacity:0}to{opacity:1}}
.pz-rise{animation:pzRise 1.3s cubic-bezier(.2,.8,.2,1) both}
@keyframes pzRise{from{opacity:0;filter:blur(14px);transform:translateY(46px)}to{opacity:1;filter:blur(0);transform:translateY(0)}}
.pz-reveal3{animation:pzReveal3 .7s cubic-bezier(.2,.8,.2,1) both}
@keyframes pzReveal3{from{opacity:0;filter:blur(10px);transform:scale(1.15)}to{opacity:1;filter:blur(0);transform:scale(1)}}
.pz-reveal2{animation:pzReveal2 .9s cubic-bezier(.2,.8,.2,1) both}
@keyframes pzReveal2{from{opacity:0;filter:blur(16px);transform:scale(1.3) translateY(16px)}to{opacity:1;filter:blur(0);transform:scale(1) translateY(0)}}
.pz-hero-name{animation:pzHero 1.7s cubic-bezier(.2,.8,.2,1) both}
@keyframes pzHero{from{opacity:0;filter:blur(26px) brightness(2.2);transform:scale(1.7);letter-spacing:26px}to{opacity:1;filter:blur(0) brightness(1);transform:scale(1);letter-spacing:2px}}
.pz-hero-amount{animation:pzHero 1.7s cubic-bezier(.2,.8,.2,1) .45s both}
.pz-crown{display:inline-block;animation:pzCrown 1.1s cubic-bezier(.2,.9,.3,1.3) .9s both}
@keyframes pzCrown{from{opacity:0;transform:translateY(-90px) rotate(-16deg)}to{opacity:1;transform:translateY(0) rotate(0)}}
.pz-flash{position:absolute;inset:0;background:radial-gradient(closest-side,rgba(255,210,74,.5),transparent 70%);animation:pzFlash 1.4s ease both;pointer-events:none}
@keyframes pzFlash{0%{opacity:0}30%{opacity:.9}100%{opacity:0}}
.pz-confetti{position:absolute;top:-26px;border-radius:2px;will-change:transform;pointer-events:none}
@keyframes pzConfetti{to{transform:translateY(112vh) rotate(720deg)}}
.pz-guide{position:absolute;left:0;right:0;bottom:24px;text-align:center;font-size:12px;letter-spacing:4px;color:#7c8ab0;text-transform:uppercase;animation:pzPulse 1.8s ease-in-out infinite;pointer-events:none}
@keyframes pzPulse{0%,100%{opacity:.32}50%{opacity:.78}}
.pz-btn{position:absolute;top:18px;background:rgba(255,255,255,.08);color:#cdd6ee;border:1px solid rgba(255,255,255,.16);border-radius:10px;width:42px;height:42px;font-size:16px;cursor:pointer;z-index:6;display:flex;align-items:center;justify-content:center}
.pz-x{right:18px}.pz-reset{left:18px}
.pz-stage{position:absolute;inset:0;animation:pzFade .8s ease both}
.pz-stitle{position:absolute;top:5%;left:0;right:0;text-align:center;font-family:var(--au-font-text);font-weight:800;font-size:clamp(20px,3.4vw,34px)}
.pz-legend{position:absolute;top:calc(5% + 46px);left:0;right:0;display:flex;gap:22px;justify-content:center;font-size:13px;color:#aab6d4}
.pz-floor{position:absolute;left:50%;bottom:9%;width:160vw;height:64vh;transform:translateX(-50%) rotateX(58deg);transform-origin:center bottom;background-image:linear-gradient(rgba(130,150,210,.10) 1px,transparent 1px),linear-gradient(90deg,rgba(130,150,210,.10) 1px,transparent 1px);background-size:58px 58px;pointer-events:none;mask-image:radial-gradient(closest-side,#000 55%,transparent);-webkit-mask-image:radial-gradient(closest-side,#000 55%,transparent)}
.pz-cols{position:absolute;left:0;right:0;bottom:12%;display:flex;align-items:flex-end;justify-content:center;filter:drop-shadow(0 26px 26px rgba(0,0,0,.5))}
.pz-cell{display:flex;flex-direction:column;align-items:center;justify-content:flex-end}
.pz-label{text-align:center;margin-bottom:12px}
.pz-beat{animation:pzBeat 1.4s ease-in-out infinite}
@keyframes pzBeat{0%,100%{transform:scale(1)}50%{transform:scale(1.035)}}
`;

// ── Medalla: 👑 dorada (#1), plata CSS (#2), bronce CSS (#3), #N (resto) ──
function MedalBadge({ pos, size = 32 }) {
  if (pos === 0) return <span style={{ fontSize: Math.round(size * 1.15), filter: 'drop-shadow(0 0 10px rgba(255,210,74,.7))', lineHeight: 1 }}></span>;
  if (pos === 1 || pos === 2) {
    const g = pos === 1 ? 'linear-gradient(145deg,#f3f4f6,#e5e7eb 42%,#9ca3af)' : 'linear-gradient(145deg,#f4b56b,#d97706 46%,#92400e)';
    const ring = pos === 1 ? '#cbd2dc' : '#7c3d0a';
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: RADIO_AVATAR, background: g, border: `2px solid ${ring}`, color: '#fff', fontWeight: 800, fontFamily: 'var(--au-font-text)', fontSize: Math.round(size * 0.52), boxShadow: 'none', textShadow: '0 1px 2px rgba(0,0,0,.45)' }}>{pos + 1}</span>
    );
  }
  return <span style={{ fontFamily: 'var(--au-font-text)', fontWeight: 800, fontSize: Math.round(size * 0.62), color: '#c3cae0' }}>#{pos + 1}</span>;
}

// ── Columna 3D (técnica skew: frente + costado skewY(-45) + tapa skewX(-45)) ──
function Columna({ d, W, D, gap, maxTotal, grown, pos, nameFs, amtFs, labelW }) {
  const total = Math.max(0, Number(d.total || 0));
  const casa = Math.max(0, Number(d.casa || 0));
  const rp = Math.max(0, Number(d.rp || 0));
  const suma = casa + rp || total || 1;
  const barMaxVh = 34;
  const Hvh = maxTotal > 0 ? (total / maxTotal) * barMaxVh : 4;
  const casaFrac = suma > 0 ? casa / suma : 1;
  const rpFrac = suma > 0 ? rp / suma : 0;
  const tapaRP = rp > 0;
  const isTop = pos === 0;

  return (
    <div className={`pz-cell${isTop ? ' pz-beat' : ''}`} style={{ margin: `0 ${Math.round(gap / 2)}px`, filter: isTop ? 'drop-shadow(0 0 22px rgba(255,210,74,.55))' : 'none' }}>
      {/* ARRIBA: solo medalla / #ranking */}
      <div style={{ marginBottom: 10, height: 40, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
        <MedalBadge pos={pos} size={isTop ? 40 : 32} />
      </div>
      {/* Barra (frente + costado + tapa 3D) */}
      <div style={{ position: 'relative', width: W, height: `${grown ? Hvh : 0}vh`, transition: 'height 1.6s cubic-bezier(.2,.8,.2,1)' }}>
        <div style={{ position: 'absolute', top: 0, left: '100%', width: D, height: '100%', transform: 'skewY(-45deg)', transformOrigin: 'left top', background: 'linear-gradient(180deg,#3b0f66,#0b3d24)', opacity: 0.9 }} />
        <div style={{ position: 'absolute', top: 0, left: 0, width: W, height: D, transform: 'translateY(-100%) skewX(-45deg)', transformOrigin: 'bottom left', background: tapaRP ? '#cf9bff' : '#5ff09a' }} />
        {rpFrac > 0 && <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: `${rpFrac * 100}%`, background: 'linear-gradient(180deg,#cf9bff 0%,#a855f7 45%,#6d1fb8 100%)' }} />}
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: `${casaFrac * 100}%`, background: 'linear-gradient(180deg,#5ff09a 0%,#22c55e 45%,#15803d 100%)' }} />
      </div>
      {/* ABAJO: nombre y monto COMPLETOS (sin truncar, hasta 2+ líneas) */}
      <div style={{ marginTop: 12, width: labelW, minHeight: 64 }}>
        <div style={{ fontFamily: 'var(--au-font-text)', fontWeight: 800, fontSize: nameFs, color: '#eef2ff', lineHeight: 1.15, wordBreak: 'break-word' }}>{d.nombre}</div>
        <div style={{ fontFamily: 'var(--au-font-text)', fontWeight: 800, fontSize: amtFs, marginTop: 3, background: 'linear-gradient(135deg,#f74780,#ff8c42)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>{money(total)}</div>
      </div>
    </div>
  );
}

export default function PresentacionShow({ data, periodoLabel, modo, onClose }) {
  const rank = useMemo(() => (data || []).filter((d) => Number(d.total) > 0), [data]);
  const pocos = rank.length < 3;
  const STAGE = 7; // paso del estadio 3D (portada 0 · T3/T2/T1 misterio+nombre 1-6 · estadio 7)
  const START = pocos ? STAGE : 0;
  const [step, setStep] = useState(START);
  const [grown, setGrown] = useState(false);
  const rootRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // Fullscreen al montar; salir cierra la presentación.
  useEffect(() => {
    const el = rootRef.current;
    if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {});
    const onFs = () => { if (!document.fullscreenElement) closeRef.current(); };
    document.addEventListener('fullscreenchange', onFs);
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, []);

  // Teclado: Esc sale, Espacio/→ avanza.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { closeRef.current(); }
      else if (e.key === ' ' || e.key === 'ArrowRight') { e.preventDefault(); setStep((s) => Math.min(7, s + 1)); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Crecimiento de columnas al entrar al estadio.
  useEffect(() => {
    if (step === 7) { const t = setTimeout(() => setGrown(true), 60); return () => clearTimeout(t); }
    setGrown(false);
  }, [step]);

  const advance = () => setStep((s) => Math.min(STAGE, s + 1));
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

  const confetti = useMemo(() => Array.from({ length: 52 }, (_, i) => ({
    left: Math.random() * 100,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    dur: 1.6 + Math.random() * 1.8,
    delay: Math.random() * 0.5,
    w: 7 + Math.random() * 7,
    h: 10 + Math.random() * 8,
    rot: Math.random() * 360,
  })), []);

  // ── Acto 1: slide de countdown (top 3/2/1) ──
  const renderSlide = () => {
    if (step === 0) {
      const periodoWord = modo === 'mes' ? (periodoLabel.split(' ')[0] || '').toUpperCase() : 'LA SEMANA';
      return (
        <div className="pz-center" key="s0">
          <div className="pz-eyebrow pz-fade">{modo === 'semana' ? 'Junta semanal' : 'Junta mensual'}</div>
          <div className="pz-name pz-fade" style={{ fontSize: 'clamp(34px,7vw,74px)', color: '#eef1fb' }}>
            ¿Quién se llevó más <span style={{ filter: 'drop-shadow(0 0 10px rgba(255,255,255,.9))' }}>{<Ic n="Banknote" chico />}</span> en <span className="pz-grad">{periodoWord}</span>?
          </div>
          <div className="pz-desglose pz-fade" style={{ marginTop: 26, fontSize: 'clamp(15px,2.4vw,26px)', color: '#cdd6ee' }}>{periodoLabel}</div>
        </div>
      );
    }
    // Pasos 1-6: cada top son 2 pasos (misterio + nombre).
    //  step 1/2 → TOP3 (idx2), 3/4 → TOP2 (idx1), 5/6 → TOP1 (idx0). Impar=misterio, par=nombre.
    const idx = 2 - Math.floor((step - 1) / 2);
    const revealed = step % 2 === 0;
    const d = rank[idx];
    if (!d) return null;
    const isTop1 = idx === 0;
    // El slide persiste entre misterio→nombre (key por top) para que solo el nombre se re-anime.
    const amtCls = isTop1 ? 'pz-amount pz-gold pz-hero-amount' : idx === 1 ? 'pz-amount pz-grad pz-rise' : 'pz-amount pz-grad pz-fade';
    const nameSz = isTop1 ? 'clamp(40px,10vw,74px)' : 'clamp(34px,7.5vw,62px)';
    const revealCls = isTop1 ? 'pz-name pz-gold pz-hero-name' : idx === 1 ? 'pz-name pz-grad pz-reveal2' : 'pz-name pz-grad pz-reveal3';
    return (
      <div className="pz-center" key={`top${idx}`}>
        {isTop1 && revealed && <div className="pz-flash" />}
        <div className="pz-eyebrow pz-fade" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
          <span>{`Top ${idx + 1}`}</span>
          {idx > 0 && <MedalBadge pos={idx} size={30} />}
        </div>
        {/* Slot del nombre: reserva espacio (corona + nombre) para que nada brinque al revelar */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
          {isTop1 && (
            <div style={{ height: 'clamp(50px,8vw,78px)', display: 'flex', alignItems: 'flex-end' }}>
              {revealed && <span className="pz-crown" style={{ fontSize: 'clamp(40px,7vw,66px)', lineHeight: 1 }}></span>}
            </div>
          )}
          <div key={revealed ? 'rv' : 'my'} className={revealed ? revealCls : 'pz-name'} style={{ fontSize: nameSz, color: revealed ? undefined : 'rgba(255,255,255,.22)', letterSpacing: revealed ? undefined : 10 }}>
            {revealed ? d.nombre : '? ? ?'}
          </div>
        </div>
        <div className={amtCls} style={{ fontSize: isTop1 ? 'clamp(30px,6vw,54px)' : 'clamp(26px,5vw,46px)' }}>{money(d.total)}</div>
        <div className="pz-desglose pz-fade">Casa {money(d.casa)} &nbsp;·&nbsp; RP {money(d.rp)}</div>
        {isTop1 && revealed && confetti.map((c, i) => (
          <div key={i} className="pz-confetti" style={{ left: `${c.left}%`, width: c.w, height: c.h, background: c.color, transform: `rotate(${c.rot}deg)`, animation: `pzConfetti ${c.dur}s linear ${c.delay}s both` }} />
        ))}
      </div>
    );
  };

  // ── Acto 2: estadio 3D ──
  const renderStage = () => {
    const asc = [...rank].sort((a, b) => Number(a.total) - Number(b.total)); // menor→mayor (izq→der)
    const rankPos = new Map(rank.map((d, i) => [d, i])); // posición por comisión desc
    const maxTotal = rank.length ? Number(rank[0].total) : 0;
    const n = rank.length;
    // Tamaño dinámico: menos vendedores = más grande; muchos = compacto (pero nada cortado).
    const t = n <= 6 ? { W: 64, D: 20, gap: 52, nameFs: 17, amtFs: 15 }
      : n <= 10 ? { W: 52, D: 16, gap: 36, nameFs: 15, amtFs: 14 }
        : n <= 16 ? { W: 40, D: 12, gap: 24, nameFs: 12.5, amtFs: 12 }
          : { W: 30, D: 10, gap: 16, nameFs: 11, amtFs: 10.5 };
    const labelW = t.W + t.gap - 8;
    return (
      <div className="pz-stage" key="stage">
        <div className="pz-stitle">{<Ic n="Star" chico />} Ranking completo · {periodoLabel}</div>
        <div className="pz-legend">
          <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: '#22c55e', marginRight: 6, verticalAlign: 'middle' }} />Casa</span>
          <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: '#a855f7', marginRight: 6, verticalAlign: 'middle' }} />RP</span>
        </div>
        <div className="pz-floor" />
        <div className="pz-cols" style={{ overflowX: 'auto', padding: '0 3vw' }}>
          {asc.map((d, i) => (
            <Columna key={i} d={d} W={t.W} D={t.D} gap={t.gap} maxTotal={maxTotal} grown={grown} pos={rankPos.get(d)} nameFs={t.nameFs} amtFs={t.amtFs} labelW={labelW} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div ref={rootRef} className="pz-root" onClick={advance}>
      <style>{CSS}</style>
      <button className="pz-btn pz-reset" onClick={stop(() => setStep(START))} title="Reiniciar">↺</button>
      <button className="pz-btn pz-x" onClick={stop(() => closeRef.current())} title="Salir">{<Ic n="X" chico />}</button>
      {step < STAGE ? renderSlide() : renderStage()}
      <div className="pz-guide">{step === STAGE ? 'Esc para salir' : (step >= 1 && step % 2 === 1) ? '¿Quién será? · CLICK para revelar…' : 'Click · Espacio · → para continuar'}</div>
    </div>
  );
}
